import { AssignmentReturned, AssignmentTurnedIn } from "@mui/icons-material";
import Add from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import BadgeIcon from "@mui/icons-material/ConfirmationNumber";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import DiamondIcon from "@mui/icons-material/Diamond";
import DoubleArrow from "@mui/icons-material/DoubleArrow";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";
import KeyIcon from "@mui/icons-material/Key";
import PublishedWithChangesIcon from "@mui/icons-material/PublishedWithChanges";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Chip,
  ChipProps,
  IconButton,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Permissions, ROLES } from "app/constants";
import useExcelUrl from "app/useExcelUrl";
import {
  Checkbox,
  DataGridV2 as DataGrid,
  DataGridBaseBaseProps,
  DataGridLinkColumn,
  DataGridModalEditColumn,
  DateRangePicker,
  GridColumns,
  PageHeader,
  Spacer,
  TextField,
  Toolbar,
  enumToOptions,
  globalMessageHandler,
} from "common";
import {
  GridState,
  UseDataGridReturnType,
} from "common/components/DataGridV2/types";
import CheckboxGroup from "common/components/Inputs/CheckboxGroup/CheckboxGroup";
import BoatCheckboxSelector from "components/BoatCheckboxSelector/BoatCheckboxSelector";
import BookingButton from "components/BookingButton/BookingButton";
import DateRangeSelector2 from "components/DateRangeSeletor/DateRangeSelector2";
import ProgramCheckboxSelector from "components/ProgramCheckboxSelector/ProgramCheckboxSelector";
import BulkEmailModal from "features/bulk-emails-sent/BulkEmailModal";
import { EVENT_STATUSES } from "features/events/eventConstants";
import { PartnerItem } from "features/partners/partnerApi";
import useAccess from "features/profile/useAccess";
import UpgradeButton from "features/upgrades/UpgradeButton/UpgradeButton";
import { getPrice } from "helpers/priceHelper";
import I18n from "i18n";
import { size } from "lodash";
import moment from "moment";
import { ReactNode, useRef, useState } from "react";
import AutoCompleteFilter from "./AutoCompleteFilter";
import { BookingItem, setRefundAt, setWebCommentHandled } from "./bookingApi";
import { BOOKING_STATUSES, PAYMENT_STATUSES } from "./bookingConstants";
import BookingForm from "./BookingForm";
import BookingReadyActions from "./BookingReadyActions";
import CreatedByFilter from "./CreatedByFilter";
import MoveBookingListModal from "./MoveBookings/MoveBookingListModal";
import PartnerGroupFilter from "./PartnerGroupFilter";
import RefundBookingListModal from "./RefundBookings/RefundBookingListModal";
import useBookingList from "./useBookingList";
export interface BookingListProps extends Omit<DataGridBaseBaseProps, "id"> {
  showHeader?: boolean;
  defaultState?: Partial<GridState>;
  isInCalendar?: boolean;
  hideCreate?: boolean;
  isPartnerView?: boolean;
  isInline?: boolean;
  id: string;
  customScope?: "shouldNotify" | "webComment" | "refundEligible";
}

export default function BookingList({
  showHeader = true,
  isInCalendar,
  defaultState,
  id,
  hideCreate,
  isPartnerView,
  customScope,
  isInline,
  ...props
}: BookingListProps) {
  const hasSections =
    customScope === "shouldNotify" ||
    customScope === "webComment" ||
    customScope === "refundEligible";
  const [activeScope, setActiveScope] = useState<string | undefined>(
    customScope,
  );
  const {
    params,
    scope,
    editUrl,
    listId,
    checkInAsync,
    checkOutAsync,
    registerArmbandAsync,
    cancelArmbandAsync,
    filter,
    gridFilter,
    onChange,
    showMore,
    setShowMore,
    cancelBookingsByIdsAsync,
    users,
    partners,
    partnergroups,
    ...service
  } = useBookingList(id, hasSections ? undefined : activeScope);
  const { hasRoles } = useAccess();
  const excelUrl = useExcelUrl("bookings", scope, gridFilter);
  const gridRef = useRef<UseDataGridReturnType<BookingItem>>();
  const [bookingsToMove, setBookingsToMove] = useState<{
    bookingIds: number[];
    programIds: number[];
  }>();
  const [bookingsToRefund, setBookingsToRefund] = useState<{
    bookingIds: number[];
    refund?: number | null;
  }>();
  const [bookingsToSendEmail, setBookingsToSendEmail] = useState<number[]>();
  const { hasPermissions, canEditItem } = useAccess();
  let columns: GridColumns<BookingItem> = [
    {
      field: "booking_number",
      headerName: I18n.t("Booking.attributes.booking_number"),
      renderCell: params => (
        <DataGridModalEditColumn
          //href={`${editUrl}${params.row?.id}`}
          params={params}
          onClick={() => {
            params.onEditClick &&
              params.onEditClick(params.row?.id, params.row);
          }}
        />
      ),
    },
    {
      field: "external_booking_number",
      headerName: I18n.t("Booking.attributes.external_booking_number"),
    },
    {
      valueFormatter: params =>
        `${moment(params?.row?.estimated_start_time).format("L LT")}`,
      //field: "event.start_time",
      field: "estimated_start_time",
      headerName: I18n.t("Event.attributes.start_time"),
      renderCell: params =>
        params?.row?.estimated_start_time
          ? moment(params?.row?.estimated_start_time).format("L LT")
          : null,
    },
    {
      field: "event.boat.name",
      hideable: true,
      sortable: false,
      headerName: I18n.t("Event.attributes.boat_id"),
      /*renderCell: params => (
        <DataGridLinkColumn
          href={`/boats/${params.row?.event?.boat_id}`}
          params={params}
        />
      ),*/
    },
    {
      field: "event.program.name",
      hide: true,
      hideable: true,
      headerName: I18n.t("Event.attributes.program_id"),
      /*renderCell: params => (
        <DataGridLinkColumn
          href={`/programs/${params.row?.event?.program_id}`}
          params={params}
        />
      ),*/
    },
    {
      field: "event.schedule.{program.name} {date_from}-{date_to}",
      hide: true,
      hideable: true,
      headerName: I18n.t("Event.attributes.schedule_id"),
      /*renderCell: params => (
        <DataGridLinkColumn
          href={`/schedules/${params.row?.event?.schedule_id}`}
          params={params}
        />
      ),*/
    },
    {
      hide: !!params.program_variant_id,
      hideable: !params.program_variant_id,
      field: "program_variant.name",
      headerName: I18n.t("Booking.attributes.program_variant_id"),
      renderCell: params => (
        <Toolbar sx={{ gap: 1 }}>
          <Box
            sx={{
              width: 10,
              minWidth: 10,
              height: 10,
              backgroundColor: params.row.program_variant?.program?.color,
              borderRadius: "5px",
            }}
          />
          {params.formattedValue}
        </Toolbar>
      ),
    },
    {
      field: "program_variant.program.name",
      hide: true,
      hideable: true,
      headerName: I18n.t("ProgramVariant.attributes.program_id"),
      renderCell: params => (
        <DataGridLinkColumn
          href={`/programs/${params.row?.program_variant?.program_id}`}
          params={params}
        />
      ),
    },
    {
      field: "custom_partner",
      headerName: I18n.t("Booking.attributes.custom_partner"),
      type: "boolean",
      hideable: true,
    },
    /*{
      field: "partner_name",
      headerName: I18n.t("Booking.attributes.partner_name"),
      hideable: true,
      flex: 1,
    },*/
    {
      hide: !!params.partner_id,
      hideable: !params.partner_id,
      field: "partner.name",
      headerName: I18n.t("Booking.attributes.partner_id"),
      renderCell: params => {
        if (params?.row?.custom_partner) {
          return params?.row?.partner_name;
        }
        return params?.row?.partner?.name;
      },
      headerActions: (
        <AutoCompleteFilter<PartnerItem>
          filterKey="partner"
          filterKey2="partnerName"
          filter={filter}
          label={I18n.t("Booking.attributes.partner_id")}
          keywordLabel={"Partner név"}
          keywordPlaceholder="Keresés partner név alapján"
          onChange={onChange}
          options={partners}
          title="Forrás szűrő"
          getOptionLabel={(option: PartnerItem) => option.name}
          getOptionValue={(option: PartnerItem) => option.id}
        />
      ),
    },
    {
      field: "partner.partner_group.name",
      //hide: true,
      hideable: true,
      headerName: I18n.t("Partner.attributes.partner_group_id"),
      headerActions: (
        <PartnerGroupFilter
          onChange={onChange}
          partnerGroups={partnergroups}
          filter={filter}
          title={I18n.t("Partner.attributes.partner_group_id")}
        />
      ),
    },
    {
      field: "source.name",
      //hide: true,
      hideable: true,
      headerName: I18n.t("Booking.attributes.source_id"),
      headerActions: (
        <AutoCompleteFilter<PartnerItem>
          filterKey="source"
          filter={filter}
          onChange={onChange}
          options={partners}
          title="Forrás szűrő"
          getOptionLabel={(option: PartnerItem) => option.name}
          getOptionValue={(option: PartnerItem) => option.id}
        />
      ),
    },
    {
      field: "created_by_partner.name",
      hide: isPartnerView,
      hideable: !isPartnerView,
      headerName: I18n.t("Booking.attributes.created_by_partner_id"),
      headerActions: !isPartnerView ? (
        <AutoCompleteFilter<PartnerItem>
          filterKey="created_by_partner_id"
          filter={filter}
          onChange={onChange}
          options={partners}
          title="Létrehozta partner szűrő"
          getOptionLabel={(option: PartnerItem) => option.name}
          getOptionValue={(option: PartnerItem) => option.id}
        />
      ) : undefined,
    },
    {
      field: "status",
      headerName: I18n.t("Booking.attributes.status"),
      type: "singleSelect",
      valueOptions: enumToOptions(BOOKING_STATUSES, "Booking.options.status"),
    },
    { field: "name", headerName: I18n.t("Booking.attributes.name"), flex: 1 },
    { field: "email", headerName: I18n.t("Booking.attributes.email"), flex: 1 },
    {
      field: "phone",
      headerName: I18n.t("Booking.attributes.phone"),
      hideable: true,
      flex: 1,
    },
    {
      field: "number_of_adults",
      headerName: I18n.t("Booking.attributes.number_of_adults"),
      hideable: true,
      type: "number",
    },
    {
      field: "number_of_kids",
      headerName: I18n.t("Booking.attributes.number_of_kids"),
      hideable: true,
      type: "number",
    },
    {
      field: "number_of_babies",
      headerName: I18n.t("Booking.attributes.number_of_babies"),
      hideable: true,
      type: "number",
    },
    {
      field: "number_of_guides",
      headerName: I18n.t("Booking.attributes.number_of_guides"),
      hideable: true,
      type: "number",
    },
    {
      field: "number_of_guests",
      headerName: I18n.t("Booking.attributes.number_of_guests"),
      type: "number",
    },
    {
      field: "description",
      headerName: I18n.t("Booking.attributes.description"),
      hideable: true,
      renderCell: params => {
        return (
          <Box
            sx={{
              overflow: "visible",
              textOverflow: "unset",
              whiteSpace: "wrap",
            }}
          >
            {params.formattedValue}
          </Box>
        );
      },
    },
    {
      field: "paid",
      headerName: I18n.t("Booking.attributes.paid"),
      type: "boolean",
      hideable: true,
      renderCell: params => {
        const remaining = getPrice(params.row).remaining;
        const fullyPaid = remaining <= 0;
        let color: ChipProps["color"] = "warning";
        let icon: ReactNode = <DriveFileRenameOutlineIcon />;
        let label = "Fizetetlen";
        if (params.row?.paid && fullyPaid) {
          color = "success";
          icon = <CheckIcon />;
          label = "Fizetve";
        } else if (params.row?.paid && !fullyPaid) {
          color = "warning";
          icon = <DiamondIcon />;
          label = "Nem teljesen fizetett";
        } else {
          color = "error";
          icon = <CloseIcon />;
          label = "Fizetetlen";
        }

        return (
          <Chip
            //className={rootClassName}
            //classes={{ icon: classes.icon }}
            size="small"
            icon={icon}
            label={label}
            color={color}
          />
        );
      },
    },
    {
      field: "payment_status",
      headerName: I18n.t("Booking.attributes.payment_status"),
      hideable: true,
      type: "singleSelect",
      valueOptions: enumToOptions(
        PAYMENT_STATUSES,
        "Booking.options.payment_status",
      ),
      renderCell: params => {
        const status: PAYMENT_STATUSES | null | undefined =
          params.row?.payment_status;
        if (!status) return null;
        const colorMap: Record<PAYMENT_STATUSES, ChipProps["color"]> = {
          [PAYMENT_STATUSES.CREATED]: "default",
          [PAYMENT_STATUSES.PENDING]: "warning",
          [PAYMENT_STATUSES.PAID]: "success",
          [PAYMENT_STATUSES.FAILED]: "error",
          [PAYMENT_STATUSES.EXPIRED]: "default",
          [PAYMENT_STATUSES.CANCELLED]: "error",
        };
        return (
          <Chip
            size="small"
            label={I18n.t(`Booking.options.payment_status.${status}`)}
            color={colorMap[status]}
          />
        );
      },
    },
    {
      field: "should_notify",
      headerName: I18n.t("Booking.attributes.should_notify"),
      hideable: true,
      type: "boolean",
    },
    {
      field: "created_at",
      headerName: I18n.t("App.created_at"),
      hideable: true,
      type: "datetime",
    },
    {
      field: "voucher",
      headerName: I18n.t("Booking.attributes.voucher"),
      hideable: true,
    },
    {
      field: "pay_location",
      headerName: I18n.t("Booking.attributes.pay_location"),
      hideable: true,
    },
    {
      field: "created_by_name",
      headerName: I18n.t("App.created_by_name"),
      hideable: true,
      sortable: false,
      headerActions: (
        <CreatedByFilter
          onChange={onChange}
          users={users}
          filter={filter}
          title={I18n.t("App.created_by_name")}
        />
      ),
    },
    {
      field: "updated_at",
      headerName: I18n.t("App.updated_at"),
      hideable: true,
      type: "datetime",
    },
    {
      field: "updated_by_name",
      headerName: I18n.t("App.updated_by_name"),
      hideable: true,
    },
    {
      field: "is_group",
      headerName: I18n.t("Booking.attributes.is_group"),
      type: "boolean",
      hideable: true,
    },
    {
      field: "check_in_at",
      headerName: I18n.t("Booking.attributes.check_in_at"),
      hideable: true,
      type: "datetime",
    },
    {
      field: "armband_at",
      headerName: I18n.t("Booking.attributes.armband_at"),
      hideable: true,
      type: "datetime",
    },
    {
      field: "booking_ready_at",
      headerName: I18n.t("Booking.attributes.booking_ready_at"),
      hideable: true,
      type: "datetime",
    },
    {
      field: "food_ready_at",
      headerName: I18n.t("Booking.attributes.food_ready_at"),
      hideable: true,
      type: "datetime",
    },
    {
      field: "description",
      headerName: I18n.t("Booking.attributes.description"),
      hideable: true,
      hide: true,
    },
    {
      field: "sku",
      headerName: I18n.t("Booking.attributes.sku"),
      hideable: true,
      flex: 1,
    },
    {
      field: "adult_price",
      headerName: I18n.t("Booking.attributes.adult_price"),
      type: "currency",
      currencyOptions: {
        precision: 2,
        unit: "€",
        format: "%u %n",
        separator: ",",
        delimiter: " ",
        stripInsignificantZeros: true,
      },
      hideable: true,
      align: "right",
    },
    {
      field: "kid_price",
      headerName: I18n.t("Booking.attributes.kid_price"),
      type: "currency",
      currencyOptions: {
        precision: 2,
        unit: "€",
        format: "%u %n",
        separator: ",",
        delimiter: " ",
        stripInsignificantZeros: true,
      },
      hideable: true,
      align: "right",
    },
    {
      field: "refund_amount",
      headerName: I18n.t("Booking.attributes.refund_amount"),
      type: "currency",
      currencyOptions: {
        precision: 2,
        unit: "€",
        format: "%u %n",
        separator: ",",
        delimiter: " ",
        stripInsignificantZeros: true,
      },
      hideable: true,
      align: "right",
    },
    {
      field: "rebook_price",
      headerName: I18n.t("Booking.attributes.rebook_price"),
      type: "currency",
      currencyOptions: {
        precision: 2,
        unit: "€",
        format: "%u %n",
        separator: ",",
        delimiter: " ",
        stripInsignificantZeros: true,
      },
      hideable: true,
      align: "right",
    },
    {
      field: "rebook_paid",
      headerName: I18n.t("Booking.attributes.rebook_paid"),
      type: "boolean",
      hideable: true,
    },
    {
      field: "event_status",
      headerName: I18n.t("Booking.attributes.event_status"),
      type: "singleSelect",
      valueOptions: enumToOptions(
        EVENT_STATUSES,
        "Event.options.status",
      ).concat([{ value: "deleted", label: "Törölt" }]),
      hideable: true,
    },
  ];

  //remove columns if
  if (hasRoles(ROLES.PARTNER)) {
    columns = columns.filter(
      c =>
        ![
          "admin_fee",
          "planned",
          "planned_event_id",
          "planned_datetime",
          "external_booking_number",
          "table_numbers",
          "voucher",
          "pay_location",
          "upgrade",
          "upgrade_price",
          "sales_person_id",
          "refund_amount",
          "refund_reason",
          "upgrade_payable",
          "upgrade_paid",
          "rebook_price",
          "rebook_paid",
          "currency",
          "admin_fee",
          "source_id",
          "payment_options",
          "lang",
          "should_notify",
          "notify_description",
          "updated_by_name",
          "armband_at",
          "is_group",
          "sku",
          //'event.status',
          "boat",
          "updated_by",
          "partner_name",
          "partner_id",
          "partner",
          "planned_event",
          //"paid",
          "created_by",
          "event_status",
          "source",
          "source_id",
          "customer_notify",
          "custom_partner",
          "boat.name",
          "event.boat.name",
          "partner.name",
          "source.name",
          "partner.partner_group.name",
          "booking_ready_at",
          "food_ready_at",
        ].includes(c.field as string),
    );
  }

  const theme = useTheme();
  const smDown = useMediaQuery(theme.breakpoints.down("sm"));
  async function openMoveBookings(
    bookingIds: any[],
    load: () => void,
    state: GridState<BookingItem>,
  ) {
    //console.log({ openMoveBookings: bookingIds });
    const programIds = state.data
      .filter(b => bookingIds.includes(b.id))
      .map(b => b.program_variant?.program_id as number);
    setBookingsToMove({ bookingIds, programIds });
  }
  async function openRefundBookings(
    bookingIds: any[],
    load: () => void,
    state: GridState<BookingItem>,
    refund?: number | null,
  ) {
    setBookingsToRefund({ bookingIds, refund });
  }

  async function openBulkEmail(
    bookingIds: any[],
    load: () => void,
    state: GridState<BookingItem>,
  ) {
    setBookingsToSendEmail(bookingIds);
  }

  return (
    <>
      {isPartnerView && !showHeader && (
        <Box
          sx={{
            //flexWrap: "wrap",
            display: "flex",
            justifyContent: "flex-start",
            px: smDown ? 2 : 0,
          }}
        >
          <DateRangeSelector2
            isClearable
            numberOfCalendars={1}
            numberOfYears={1}
            value={
              filter?.range?.start && filter?.range?.end
                ? {
                    start: moment(filter.range.start),
                    end: moment(filter.range.end),
                  }
                : undefined
            }
            onChange={(start, end) => {
              onChange({
                range:
                  start && end
                    ? {
                        start: start.format("YYYY-MM-DD"),
                        end: end.format("YYYY-MM-DD"),
                      }
                    : undefined,
              });
            }}
            dateButtonMinWidth={150}
          />
        </Box>
      )}
      {showHeader && (
        <Box
          sx={{
            //flexWrap: "wrap",
            px: smDown ? 2 : 0,
          }}
        >
          <PageHeader
            avatar={customScope ? <PublishedWithChangesIcon /> : <KeyIcon />}
            title={
              customScope
                ? I18n.t(`Booking.${customScope}Title`)
                : I18n.t("Booking.title")
            }
            sx={
              {
                //flexWrap: "wrap",
              }
            }
          >
            {smDown ? (
              <DateRangeSelector2
                isClearable
                numberOfCalendars={1}
                numberOfYears={1}
                value={
                  filter?.range?.start && filter?.range?.end
                    ? {
                        start: moment(filter.range.start),
                        end: moment(filter.range.end),
                      }
                    : undefined
                }
                onChange={(start, end) => {
                  onChange({
                    range:
                      start && end
                        ? {
                            start: start.format("YYYY-MM-DD"),
                            end: end.format("YYYY-MM-DD"),
                          }
                        : undefined,
                  });
                }}
                dateButtonMinWidth={150}
              />
            ) : (
              <DateRangePicker
                value={{ from: filter?.range?.start, to: filter?.range?.end }}
                defaultRangeIdStorageKey="bookings_default_range"
                clearBehavior="empty"
                onChange={({ from, to }) =>
                  onChange({
                    range:
                      from && to
                        ? {
                            start: from as any,
                            end: to as any,
                          }
                        : undefined,
                  })
                }
              />
            )}
          </PageHeader>
          <BoatCheckboxSelector
            value={filter?.boatIds}
            color={theme.palette.primary.main}
            onChange={evt => {
              onChange({ boatIds: (evt.target.value as any) || [] });
            }}
          />
          <Spacer h={2} />
          <ProgramCheckboxSelector
            value={filter?.programIds}
            onChange={evt => {
              console.log({ programIds: evt.target.value });
              onChange({ programIds: (evt.target.value as any) || [] });
            }}
            showMore={showMore}
            setShowMore={setShowMore}
          />
          <Spacer h={2} />
        </Box>
      )}
      <DataGrid
        sections={
          customScope === "shouldNotify"
            ? [
                {
                  value: "shouldNotify",
                  label: I18n.t("Booking.sections.shouldNotify"),
                },
                {
                  value: "notified",
                  label: I18n.t("Booking.sections.notified"),
                },
              ]
            : customScope === "webComment"
              ? [
                  {
                    value: "webComment",
                    label: I18n.t("Booking.sections.webComment"),
                  },
                  {
                    value: "webCommentHandled",
                    label: I18n.t("Booking.sections.webCommentHandled"),
                  },
                ]
              : customScope === "refundEligible"
                ? [
                    {
                      value: "refundEligible",
                      label: I18n.t("Booking.sections.refundEligible"),
                    },
                    {
                      value: "refundHandled",
                      label: I18n.t("Booking.sections.refundHandled"),
                    },
                  ]
                : undefined
        }
        onSectionChange={hasSections ? setActiveScope : undefined}
        renderCreateItem={
          isInCalendar
            ? undefined
            : ({ handleCreate }) => {
                return (
                  <BookingButton
                    buttonType="icon"
                    onSelect={(programId, event, planned, datetime) => {
                      if (planned) {
                        handleCreate({} as any, {
                          //event_id: event.id,
                          planned_datetime: datetime,
                          planned_event: event,
                          planned_event_id: event?.id,
                          planned: true,
                          status: EVENT_STATUSES.ACTIVE,
                        });
                      } else {
                        handleCreate({} as any, {
                          event_id: event?.id,
                          event: event,
                          status: EVENT_STATUSES.ACTIVE,
                        });
                      }
                    }}
                  >
                    <Add />
                  </BookingButton>
                );
              }
        }
        renderToolbarLeftItems={({ handleCreate }) => (
          <Toolbar flexWrap="wrap">
            <CheckboxGroup
              options={enumToOptions(
                BOOKING_STATUSES,
                "Booking.options.status",
              ).filter(o => o.value !== BOOKING_STATUSES.PLANNED)}
              onChange={(evt: any) => onChange({ status: evt.target.value })}
              value={filter?.status}
            />
            {showHeader && (
              <CheckboxGroup
                options={[
                  { value: "scheduled", label: "Menetrend" },
                  { value: "planned", label: "Előfoglalás" },
                ]}
                onChange={(evt: any) => onChange({ types: evt.target.value })}
                value={filter?.types}
                color="secondary"
              />
            )}
            <Checkbox
              sx={{ pl: 0 }}
              label="Refund"
              value={filter?.refund}
              onChange={(evt: any) => onChange({ refund: evt.target.value })}
            />
            <Checkbox
              sx={{ pl: 0 }}
              title="Visszavont esemény"
              label="Visszavont esemény"
              value={filter?.eventCancelled}
              onChange={(evt: any) =>
                onChange({ eventCancelled: evt.target.value })
              }
            />
            {!isPartnerView && (
              <Checkbox
                title="Csoportos esemény"
                sx={{ pl: 0 }}
                label="Csoport"
                value={filter?.is_group}
                onChange={(evt: any) =>
                  onChange({ is_group: evt.target.value })
                }
              />
            )}
            <TextField
              value={filter?.startTime}
              fullWidth={false}
              placeholder="időpont: 17:00 vagy 17"
              onChange={(evt: any) => onChange({ startTime: evt.target.value })}
            />
          </Toolbar>
        )}
        {...service}
        filter={gridFilter}
        columns={columns}
        id={listId}
        apiRef={gridRef}
        renderModalForm={props => (
          <BookingForm {...props} isPartnerView={isPartnerView} />
        )}
        modalProps={{ maxWidth: "lg", fullScreen: smDown }}
        {...props}
        //getHeaderName={field => I18n.t(`Booking.attributes.${field.replaceAll(".", "_")}`)}
        rowSx={(item: BookingItem) => {
          let baseSx: any = {};

          if (item.should_notify) {
            baseSx.position = "relative";
            baseSx["& td:first-of-type::before"] = {
              content: '""',
              position: "absolute",
              left: 50,
              top: "50%",
              transform: "translateY(-50%)",
              width: 12,
              height: 12,
              borderRadius: "50%",
              backgroundColor: (theme: any) => theme.palette.info.main,
              boxShadow: (theme: any) =>
                `0 0 0 1px ${theme.palette.common.white}`,
              zIndex: 10,
              pointerEvents: "none",
            };
          }

          if (item.refund_eligible) {
            return {
              ...baseSx,
              opacity: 0.5,
              filter: "grayscale(0.6)",
            };
          }
          if (item.status === BOOKING_STATUSES.CANCELED) {
            return {
              ...baseSx,
              backgroundColor: theme => alpha(theme.palette.error.main, 0.15),
            };
          }
          if (item.planned) {
            return {
              ...baseSx,
              backgroundColor: theme => alpha(theme.palette.warning.main, 0.15),
            };
          }
          if (!!item.check_in_at) {
            return {
              ...baseSx,
              backgroundColor: theme => alpha(theme.palette.success.main, 0.15),
            };
          }
          return baseSx;
        }}
        defaultState={{
          sorting: [
            { field: "status", direction: "asc" },
            { field: "created_at", direction: "desc" },
          ],
          scope: customScope,
          hidden_columns: [
            "event_id",
            "boat_id",
            "program_id",
            "schedule_id",
            "custom_partner",
            "partner_name",
            "partner_group_id",
            "phone",
            "email",
            "number_of_adults",
            "number_of_kids",
            "number_of_babies",
            "number_of_guides",
            "check_in_at",
            "armband_at",
            ...(customScope === "webComment" ? ["boat.name"] : ["description"]),
            ...(customScope === "refundEligible"
              ? ["boat", "description"]
              : ["description"]),
            "sku",
            "adult_price",
            "kid_price",
            "created_at",
            "updated_at",
            "updated_by_name",
            "voucher",
            "pay_location",
            "event_status",
            "refund_amount",
            "should_notify",
          ],
          ...defaultState,
        }}
        canCreate={
          !isPartnerView && !hideCreate && hasPermissions("booking.create")
        }
        canDelete={hasPermissions(["booking.editany", "booking.editown"])}
        canEdit={hasPermissions(["booking.editany", "booking.editown"])}
        canDeleteRow={params => canEditItem("booking", params.row)}
        canEditRow={params => canEditItem("booking", params.row)}
        canBatchActions={
          !isPartnerView &&
          hasPermissions(["booking.editany", "booking.editown"])
        }
        canHandleTrash={hasPermissions(["booking.editany", "booking.editown"])}
        rowActions={(params, openEdit, refresh) => {
          let actions: any[] = [];
          if (customScope === "webComment") {
            const isHandled = activeScope === "webCommentHandled";
            actions.push(
              <IconButton
                key="web-comment-handled"
                size="small"
                title={I18n.t(
                  isHandled
                    ? "Booking.webCommentUnhandledButton"
                    : "Booking.webCommentHandledButton",
                )}
                onClick={async () => {
                  const { success } = await setWebCommentHandled(
                    params.row.id,
                    !isHandled,
                  );
                  if (success) {
                    globalMessageHandler.snack({
                      message: I18n.t(
                        isHandled
                          ? "Booking.webCommentUnhandledSuccess"
                          : "Booking.webCommentHandledSuccess",
                      ),
                      severity: "success",
                    });
                    refresh();
                  }
                }}
              >
                {isHandled ? <CloseIcon /> : <CheckIcon />}
              </IconButton>,
            );
          }
          if (customScope === "refundEligible") {
            const isHandled = activeScope === "refundHandled";
            actions.push(
              <IconButton
                key="refund-handled"
                size="small"
                title={I18n.t(
                  isHandled
                    ? "Booking.refundHandledUndoButton"
                    : "Booking.refundHandledButton",
                )}
                onClick={async () => {
                  const { success } = await setRefundAt(
                    params.row.id,
                    isHandled ? false : true,
                  );
                  if (success) {
                    globalMessageHandler.snack({
                      message: I18n.t(
                        isHandled
                          ? "Booking.refundHandledUndoSuccess"
                          : "Booking.refundHandledSuccess",
                      ),
                      severity: "success",
                    });
                    refresh();
                  }
                }}
              >
                {isHandled ? <CloseIcon /> : <CheckIcon />}
              </IconButton>,
            );
          }
          const showMoreActions = !(
            customScope &&
            ["refundEligible", "webComment"].includes(customScope)
          );
          if (!isPartnerView && showMoreActions) {
            if (!params.row?.planned) {
              actions.push(
                <UpgradeButton
                  booking={params.row}
                  buttonType="icon"
                  key="upgrade"
                  size="small"
                  afterUpgrade={item => {
                    refresh();
                  }}
                >
                  <DiamondIcon />
                </UpgradeButton>,
              );

              actions.push(
                <IconButton
                  key="move"
                  size="small"
                  title={I18n.t("Booking.moveTitle")}
                  onClick={() => {
                    setBookingsToMove({
                      bookingIds: [params.row?.id],
                      programIds: [params.row?.program_variant?.program_id],
                    });
                  }}
                >
                  <DoubleArrow />
                </IconButton>,
              );
              if (!isInline) {
                actions.push(
                  <IconButton
                    key="reg"
                    size="small"
                    title={
                      !!params.row?.check_in_at
                        ? "Vendég érkeztetés törlése"
                        : "Vendég érkeztetés"
                    }
                    onClick={() => {
                      if (!!params.row?.check_in_at) {
                        globalMessageHandler.confirm(
                          {
                            message: "Biztosan törlöd a vendég érkeztetést?",
                          },
                          async () => {
                            await checkOutAsync(params.row);
                            refresh();
                          },
                        );
                      } else {
                        globalMessageHandler.confirm(
                          {
                            message: `Biztosan regisztrálod az alábbi foglalást? ${params.row.name}`,
                          },
                          async () => {
                            await checkInAsync(params.row);
                            refresh();
                          },
                        );
                      }
                    }}
                  >
                    {!!params.row?.check_in_at ? (
                      <AssignmentTurnedIn color="info" />
                    ) : (
                      <AssignmentReturned />
                    )}
                  </IconButton>,
                );
                actions.push(
                  <IconButton
                    key="armband"
                    size="small"
                    title={
                      !!params.row?.armband_at
                        ? "Karszalag törlése"
                        : "Karszalag regisztrálása"
                    }
                    onClick={() => {
                      if (!!params.row?.armband_at) {
                        globalMessageHandler.confirm(
                          {
                            message: "Biztosan törlöd a karszalagot?",
                          },
                          async () => {
                            await cancelArmbandAsync(params.row);
                            refresh();
                          },
                        );
                      } else {
                        globalMessageHandler.confirm(
                          {
                            message: `Biztosan regisztrálod a karszalagot az alábbi vendéghez? ${params.row.name}`,
                          },
                          async () => {
                            await registerArmbandAsync(params.row);
                            refresh();
                          },
                        );
                      }
                    }}
                  >
                    <BadgeIcon
                      color={!!params.row?.armband_at ? "info" : undefined}
                    />
                  </IconButton>,
                );
              }
              // Add ready tracking actions if program supports it
              if (
                params.row?.event?.program?.ready_tracking &&
                hasPermissions([Permissions.BookingReadyCheck]) &&
                !isInline
              ) {
                actions.push(
                  <BookingReadyActions
                    key="ready"
                    booking={params.row}
                    variant="icon"
                    onSuccess={() => {
                      refresh();
                    }}
                  />,
                );
              }
            }
          }
          return actions;
        }}
        excelUrl={excelUrl}
        bulkActions={
          isPartnerView
            ? undefined
            : [
                {
                  id: "moveBookings",
                  label: I18n.t("Booking.moveTitlePlural"),
                  action: openMoveBookings,
                  icon: <DoubleArrow />,
                },
                {
                  id: "refundBookings",
                  label: I18n.t("Booking.refundTitlePlural"),
                  action: openRefundBookings,
                  icon: <CurrencyExchangeIcon />,
                },
                {
                  id: "bulkEmail",
                  label: I18n.t("BulkEmail.titleByBooking"),
                  action: openBulkEmail,
                  icon: <SendIcon />,
                },
              ]
        }
      />
      {size(bookingsToMove?.bookingIds) > 0 && (
        <MoveBookingListModal
          bookingIds={bookingsToMove?.bookingIds}
          programId={bookingsToMove?.programIds[0]}
          open={size(bookingsToMove?.bookingIds) > 0}
          onClose={() => setBookingsToMove(undefined)}
          afterMove={() => {
            setBookingsToMove(undefined);
            if (gridRef.current) {
              //Refresh grid and clear selection
              gridRef.current.loadDeb({ selectedIds: [] });
            }
          }}
        />
      )}
      {size(bookingsToRefund?.bookingIds) > 0 && (
        <RefundBookingListModal
          bookingIds={bookingsToRefund?.bookingIds}
          open={size(bookingsToRefund?.bookingIds) > 0}
          onClose={() => setBookingsToRefund(undefined)}
          afterRefund={() => {
            setBookingsToRefund(undefined);
            if (gridRef.current) {
              //Refresh grid and clear selection
              gridRef.current.loadDeb({ selectedIds: [] });
            }
          }}
        />
      )}
      {size(bookingsToSendEmail) > 0 && (
        <BulkEmailModal
          bookingIds={bookingsToSendEmail}
          open={size(bookingsToSendEmail) > 0}
          onClose={() => setBookingsToSendEmail(undefined)}
          afterSend={() => {
            setBookingsToSendEmail(undefined);
            if (gridRef.current) {
              gridRef.current.loadDeb({ selectedIds: [] });
            }
          }}
        />
      )}
    </>
  );
}
